import { useEffect, useState, useSyncExternalStore } from 'react';
import { Link, useLocation } from 'react-router';
import { t } from '../../../shared/messages/vi';
import { signInHref } from '../../../shared/providers';
import { attemptStampFor } from './attempt-stamp';
import { useAttemptSaver } from './save-provider';
import type { AttemptDraft } from './to-attempt';
import './save-status.css';

/** What the result screen shows about saving the attempt. */
export type SavePhase = 'none' | 'checking' | 'guest' | 'saving' | 'pending' | 'saved' | 'failed';

const noSubscription = () => () => undefined;
const noVersion = () => 0;

/**
 * What was decided for a finished exam, once, when its result screen first saw a settled session:
 * a guest's result stays a guest's (it is never queued or sent, even if the session turns into a
 * signed-in one while the screen is open, e.g. a sign-in in another tab); an account's result belongs
 * to the user who was signed in then.
 */
interface Decision {
  readonly kind: 'guest' | 'account';
  readonly owner: string | null;
  enqueued: boolean;
}
const decisions = new WeakMap<object, Decision>();

/**
 * Saves the finished exam `finished` stands for (the object the screen renders the result from) and
 * reports where it stands. `build` runs inside the effect and only for an account's result, so a
 * guest's result is not even assembled. The `idempotencyKey` comes from `attemptStampFor(finished)`:
 * one per finished exam, whatever re-renders.
 */
export function useAttemptSave(finished: object, build: () => AttemptDraft): SavePhase {
  const saver = useAttemptSaver();
  const { idempotencyKey } = attemptStampFor(finished);
  const [requested, setRequested] = useState(false);

  useSyncExternalStore(saver?.subscribe ?? noSubscription, saver?.version ?? noVersion);

  let decision = decisions.get(finished);
  if (decision === undefined && saver !== null && saver.access !== 'checking') {
    decision = { kind: saver.access, owner: saver.owner, enqueued: false };
    decisions.set(finished, decision);
  }

  useEffect(() => {
    if (saver === null || decision?.kind !== 'account' || decision.owner === null) return;
    if (decision.enqueued) return;
    decision.enqueued = true;
    saver.save({ ...build(), idempotencyKey }, decision.owner);
    setRequested(true);
    // `build` describes the same finished exam on every render; the key decides what is saved.
  }, [saver, decision, idempotencyKey]);

  if (saver === null) return 'none';
  if (decision === undefined) return 'checking';
  if (decision.kind === 'guest' || decision.owner === null) return 'guest';
  const sending = saver.sendingFor(decision.owner);
  switch (saver.statusOf(idempotencyKey)) {
    case 'saved':
      return 'saved';
    case 'dead':
      return 'failed';
    case 'purged':
      return 'none';
    case 'queued':
      return sending ? 'saving' : 'pending';
    case 'unknown':
      // Gone from the queue without this tab sending it: another tab did.
      return requested ? 'saved' : sending ? 'saving' : 'pending';
  }
}

const STATUS_TEXT = {
  saving: 'history.save.saving',
  pending: 'history.save.pending',
  saved: 'history.save.saved',
  failed: 'history.save.failed',
} as const;

function GuestInvite() {
  const { pathname, search, hash } = useLocation();
  return (
    <p className="save-status save-status--guest" data-save-status="guest" data-attempt-invite>
      {t('history.save.guestInvite')}{' '}
      <Link className="save-status__link" to={signInHref(`${pathname}${search}${hash}`)}>
        {t('history.save.guestAction')}
      </Link>
    </p>
  );
}

export interface AttemptSaveStatusProps {
  /** The finished exam (sim `ExamResult`, theory `ExamState`): identity of the attempt. */
  readonly finished: object;
  readonly build: () => AttemptDraft;
}

/**
 * Saves the attempt and shows where it stands, next to the result: "Đã lưu vào lịch sử",
 * "Sẽ lưu khi có mạng", or, for a guest, an invitation to sign in that comes back to this page.
 * Renders nothing outside the provider.
 */
export function AttemptSaveStatus({ finished, build }: AttemptSaveStatusProps) {
  const phase = useAttemptSave(finished, build);
  if (phase === 'none' || phase === 'checking') return null;
  if (phase === 'guest') return <GuestInvite />;
  return (
    <p className="save-status" role="status" data-save-status={phase}>
      {t(STATUS_TEXT[phase])}
    </p>
  );
}
